import { PLATFORM_URL } from "../constants.js";
import type { ResolvedConfig } from "../config.js";
import { loadCredentials, type StoredCredentials } from "../auth/credentials.js";
import { AspError } from "./errors.js";

export type PlatformEnv = "default" | "custom";

export function detectPlatformEnv(url: string): PlatformEnv {
  const normalized = url.replace(/\/$/, "");
  if (normalized === PLATFORM_URL) return "default";
  return "custom";
}

export function formatPlatformLabel(url: string): string {
  const env = detectPlatformEnv(url);
  if (env === "default") return `platform (${PLATFORM_URL})`;
  return url;
}

export function withPlatformUrl(config: ResolvedConfig, platformUrl: string): ResolvedConfig {
  return { ...config, platformUrl: platformUrl.replace(/\/$/, "") };
}

export async function requireCredentials(
  config: ResolvedConfig,
  targetPlatformUrl: string,
): Promise<{ config: ResolvedConfig; creds: StoredCredentials }> {
  if (config.token) {
    return {
      config: withPlatformUrl(config, targetPlatformUrl),
      creds: {
        access_token: config.token,
        token_type: "Bearer",
        platform_url: targetPlatformUrl,
      },
    };
  }

  const creds = await loadCredentials();
  if (!creds?.access_token) {
    throw new AspError("Not logged in.", {
      code: "UNAUTHORIZED",
      hint: "Run `asp auth login`.",
      exitCode: 3,
    });
  }

  const credsUrl = creds.platform_url.replace(/\/$/, "");
  const targetUrl = targetPlatformUrl.replace(/\/$/, "");
  if (credsUrl !== targetUrl) {
    const loggedIn = formatPlatformLabel(credsUrl);
    const target = formatPlatformLabel(targetUrl);
    throw new AspError(`Credential environment mismatch: logged in to ${loggedIn}, command targets ${target}.`, {
      code: "ENV_MISMATCH",
      hint: "Run `asp auth login` for the current target platform.",
      exitCode: 1,
    });
  }

  return {
    config: withPlatformUrl(config, targetUrl),
    creds,
  };
}
